/**
 * Product Selection Example Page
 *
 * Demonstrates three TreeGrid grids with drag & drop functionality:
 * - Top Grid: All available products from the catalog
 * - Middle Grid: Selected products for addition to project
 * - Bottom Grid: Project Items (saved products)
 *
 * Features:
 * - Drag and drop from top grid to middle grid
 * - Delete functionality in middle grid
 * - Save button to add selected products to project items
 * - Components displayed as child rows
 * - Price calculation based on parent and child components
 */

import { useState, useMemo, useCallback, useEffect } from "react";
import { TreeGridComponent } from "../../components/ui/TreeGridComponent/TreeGridComponent";
import {
  TreeGridColumn,
  TreeGridRow,
  ComboBoxItem,
} from "../../components/ui/TreeGridComponent/types";
import { ToastQueue } from "@react-spectrum/toast";
import {
  Provider,
  defaultTheme,
  ProgressCircle,
  Flex,
  Button,
  View,
} from "@adobe/react-spectrum";
import { useDependency } from "../../contexts/DependencyProvider";
import { ListItemRequest } from "../../services/soap/item/requests/ListItemRequest";
import { GetItemRequest } from "../../services/soap/item/requests/GetItemRequest";
import { CreateProjectItemRequest } from "../../services/soap/item/requests/CreateProjectItemRequest";
import { generateProjectProductItem } from "../../services/soap/item/requests/CreateProjectItemOptions";

// Interface for product item from API
interface ProductItem {
  id: string;
  name: string;
  amount: string;
  parentAmount?: string;
  amountWithVat: string;
  quantity: string;
  purchasePrice: string;
  currency?: string;
  currencyName: string;
  vatName: string;
  vatValue: string;
  refId: string;
  unitsName: string;
  typeName: string;
  componentCategoryName: string;
  manufacturerName: string;
  statusName: string;
  COMPONENT?: ComponentItem[];
}

// Interface for component items

interface ComponentItem {
  component: string;
  componentName: string;
  amount: string;
  amountWithVat: string;
  purchasePrice: string;
  currencyName: string;
  vatName: string;
  vatValue: string;
  isParent: string;
  countMin: string;
  countMax: string;
  componentCategoryName: string;
  componentType: string;
  priceInclude: string;
  ratio: string;
}

// Transform API product to TreeGridRow for top grid (products list)
const transformProductToRow = (product: ProductItem): TreeGridRow => ({
  id: product.id,
  parentId: null,
  depth: 0,
  childrenIds: [],
  isExpanded: false,
  rowType: "product",
  sortOrder: 0,
  data: {
    productName: product.name,
    refId: product.refId || "",
    quantity: parseFloat(product.quantity) || 1,
    unitPrice: parseFloat(product.amount) || 0,
    amountWithVat: parseFloat(product.amountWithVat) || 0,
    purchasePrice: parseFloat(product.purchasePrice) || 0,
    currency: product.currencyName || "CZK",
    vatName: product.vatName || "",
    vatValue: parseFloat(product.vatValue) || 0,
    units: product.unitsName || "",
    type: product.typeName || "",
    category: product.componentCategoryName || "",
    manufacturer: product.manufacturerName || "",
    status: product.statusName || "",
    // Original data for API calls
    _original: product,
  },
});

// Transform product with components to TreeGridRows for middle grid (selected products)
const transformProductWithComponentsToRows = (
  product: any,
  components: ComponentItem[] | null,
): TreeGridRow[] => {
  const rows: TreeGridRow[] = [];
  const parentId = `selected-${product.id}-${Date.now()}`;

  // Calculate total amount including components
  let totalAmount = parseFloat(product.amount) || 0;
  let totalAmountWithVat = parseFloat(product.amountWithVat) || 0;

  // Child component rows
  const childRows: TreeGridRow[] = [];
  if (components && components.length > 0) {
    components.forEach((comp, index) => {
      // Skip parent component (isParent === '1')
      if (comp.isParent === "1") return;

      const childId = `${parentId}-child-${index}`;
      const compAmount = parseFloat(comp.amount) || 0;
      const compAmountWithVat = parseFloat(comp.amountWithVat) || 0;

      // Add component amount to total if priceInclude is '0'
      if (comp.priceInclude === "0") {
        totalAmount += compAmount;
        totalAmountWithVat += compAmountWithVat;
      }

      childRows.push({
        id: childId,
        parentId: parentId,
        depth: 1,
        childrenIds: [],
        isExpanded: false,
        rowType: "product",
        sortOrder: index,
        data: {
          productName: comp.componentName,
          refId: "",
          quantity: parseFloat(comp.countMin) || 1,
          unitPrice: compAmount,
          amountWithVat: compAmountWithVat,
          purchasePrice: parseFloat(comp.purchasePrice) || 0,
          currency: comp.currencyName || "CZK",
          vatName: comp.vatName || "",
          vatValue: parseFloat(comp.vatValue) || 0,
          units: "",
          type: "",
          category: comp.componentCategoryName || "",
          manufacturer: "",
          status: "",
          isComponent: true,
          componentId: comp.component,
          _original: comp,
        },
      });
    });
  }

  // Parent row
  rows.push({
    id: parentId,
    parentId: null,
    depth: 0,
    childrenIds: childRows.map((r) => r.id),
    isExpanded: childRows.length > 0,
    rowType: "product",
    sortOrder: 0,
    data: {
      productName: product.name,
      refId: product.refId || "",
      quantity: parseFloat(product.quantity) || 1,
      unitPrice: parseFloat(product.amount) || 0,
      totalAmount: totalAmount,
      amountWithVat: totalAmountWithVat,
      purchasePrice: parseFloat(product.purchasePrice) || 0,
      currency: product.currencyName || "CZK",
      vatName: product.vatName || "",
      vatValue: parseFloat(product.vatValue) || 0,
      units: product.unitsName || "",
      type: product.typeName || "",
      category: product.componentCategoryName || "",
      manufacturer: product.manufacturerName || "",
      status: product.statusName || "",
      hasComponents: childRows.length > 0,
      productId: product.id,
      _original: product,
    },
  });

  // Add child rows
  rows.push(...childRows);

  return rows;
};

export default function ProductSelectionExample() {
  const { store, itemService } = useDependency();

  // Loading states
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingSelected, setIsLoadingSelected] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Pagination state
  const [currentOffset, setCurrentOffset] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const PAGE_SIZE = 50;

  // Data states
  const [availableProducts, setAvailableProducts] = useState<TreeGridRow[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<TreeGridRow[]>([]);
  const [projectItems, setProjectItems] = useState<TreeGridRow[]>([]);

  // Project ID for saving (you would typically get this from route params or props)
  const [projectId, setProjectId] = useState<string>("561832101");
  // Load products from API (initial or refresh)
  const loadProducts = useCallback(
    async (reset: boolean = false) => {
      setIsLoadingProducts(true);
      try {
        const offset = reset ? 0 : currentOffset;
        const response = await itemService.listItem(
          new ListItemRequest(
            store.Server,
            store.SessionId,
            PAGE_SIZE,
            offset,
            {
              orderBy: "name",
              statusShow: "1", // Only active products
            },
          ),
        );

        //   const responseCount = await itemService.listItem(
        //     new ListItemRequest(store.Server, store.SessionId, PAGE_SIZE, offset, {
        //       orderBy: "name",
        //       statusShow: "1", // Only active products
        //       countOnly: true,
        //     })
        //   );

        if (response.result === "OK" && response.count > 0) {
          let items: ProductItem[] = [];
          if (Array.isArray(response.ITEM)) {
            items = response.ITEM as ProductItem[];
          } else if (response.ITEM) {
            items = [response.ITEM as ProductItem];
          }

          const rows = items.map(transformProductToRow);

          if (reset) {
            setAvailableProducts(rows);
            setCurrentOffset(PAGE_SIZE);
          } else {
            setAvailableProducts((prev) => [...prev, ...rows]);
            setCurrentOffset((prev) => prev + PAGE_SIZE);
          }

          const totalCount = response.count || 50;
          setTotalProducts(totalCount);
          const newOffset = reset ? PAGE_SIZE : offset + PAGE_SIZE;
          setHasMoreProducts(
            newOffset < totalCount && items.length === PAGE_SIZE,
          );

          ToastQueue.positive(`Loaded ${rows.length} products`, {
            timeout: 3000,
          });
        } else {
          if (reset) {
            setAvailableProducts([]);
          }
          setHasMoreProducts(false);
          ToastQueue.info("No products found", { timeout: 3000 });
        }
      } catch (error: any) {
        console.error("Error loading products:", error);
        ToastQueue.negative("Failed to load products", { timeout: 3000 });
      } finally {
        setIsLoadingProducts(false);
      }
    },
    [currentOffset, itemService, store.Server, store.SessionId],
  );
  // Fetch products on mount
  useEffect(() => {
    loadProducts(true);
  }, [loadProducts]);

  // Load more products (infinite scroll)
  const loadMoreProducts = useCallback(async () => {
    if (isLoadingMore || !hasMoreProducts) return;

    setIsLoadingMore(true);
    try {
      const response = await itemService.listItem(
        new ListItemRequest(
          store.Server,
          store.SessionId,
          PAGE_SIZE,
          currentOffset,
          {
            orderBy: "name",
            statusShow: "1",
          },
        ),
      );

      if (response.result === "OK" && response.count > 0) {
        let items: ProductItem[] = [];
        if (Array.isArray(response.ITEM)) {
          items = response.ITEM as ProductItem[];
        } else if (response.ITEM) {
          items = [response.ITEM as ProductItem];
        }

        const rows = items.map(transformProductToRow);
        setAvailableProducts((prev) => [...prev, ...rows]);

        const newOffset = currentOffset + PAGE_SIZE;
        setCurrentOffset(newOffset);
        setHasMoreProducts(
          newOffset < totalProducts && items.length === PAGE_SIZE,
        );
      } else {
        setHasMoreProducts(false);
      }
    } catch (error: any) {
      ToastQueue.negative("Failed to load more products", { timeout: 3000 });
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    currentOffset,
    hasMoreProducts,
    isLoadingMore,
    itemService,
    store.Server,
    store.SessionId,
    totalProducts,
  ]);

  // Fetch product details with components
  const fetchProductWithComponents = useCallback(
    async (
      productId: string,
    ): Promise<{ product: any; components: ComponentItem[] | null }> => {
      try {
        const response = await itemService.getItem(
          new GetItemRequest(store.Server, store.SessionId, productId),
        );

        if (response.result === "OK") {
          let product: any = null;
          let components: ComponentItem[] | null = null;

          if (Array.isArray(response.ITEM)) {
            product = response.ITEM[0];
          } else {
            product = response.ITEM;
          }

          // Extract components if available
          if (product?.COMPONENT) {
            if (Array.isArray(product.COMPONENT)) {
              components = product.COMPONENT;
            } else {
              components = [product.COMPONENT];
            }
          }

          return { product, components };
        }

        throw new Error("Failed to fetch product details");
      } catch (error) {
        console.error("Error fetching product details:", error);
        throw error;
      }
    },
    [itemService, store.Server, store.SessionId],
  );

  // Handle drag from available products to selected products
  const handleExternalDropToSelected = useCallback(
    async (
      rows: TreeGridRow[],
      sourceGridId: string,
      targetRowId: string | null,
      position: "before" | "after" | "child",
    ) => {
      if (sourceGridId !== "available-products") return;

      setIsLoadingSelected(true);
      try {
        const newSelectedRows: TreeGridRow[] = [];

        for (const row of rows) {
          const productId = row.id;

          // Check if product is already selected
          const alreadySelected = selectedProducts.some(
            (sp) => sp.data.productId === productId || sp.id === productId,
          );

          if (alreadySelected) {
            ToastQueue.info(`${row.data.productName} is already selected`, {
              timeout: 2000,
            });
            continue;
          }

          // Fetch product with components
          const { product, components } =
            await fetchProductWithComponents(productId);

          if (product) {
            const productRows = transformProductWithComponentsToRows(
              product,
              components,
            );
            newSelectedRows.push(...productRows);
          }
        }

        if (newSelectedRows.length > 0) {
          setSelectedProducts((prev) => [...prev, ...newSelectedRows]);
          ToastQueue.positive(`Added ${rows.length} product(s) to selection`, {
            timeout: 3000,
          });
        }
      } catch (error: any) {
        console.error("Error adding products to selection:", error);
        ToastQueue.negative("Failed to add products", { timeout: 3000 });
      } finally {
        setIsLoadingSelected(false);
      }
    },
    [selectedProducts, fetchProductWithComponents],
  );

  // Handle delete from selected products
  const handleDeleteFromSelected = useCallback(
    (rowId: string, row: TreeGridRow) => {
      // If it's a parent row, remove it and all its children
      if (row.parentId === null) {
        setSelectedProducts((prev) =>
          prev.filter((r) => r.id !== rowId && r.parentId !== rowId),
        );
      } else {
        // If it's a child, just remove the child
        setSelectedProducts((prev) => {
          const newRows = prev.filter((r) => r.id !== rowId);
          // Update parent's childrenIds
          return newRows.map((r) => {
            if (r.id === row.parentId) {
              return {
                ...r,
                childrenIds: r.childrenIds.filter((id) => id !== rowId),
              };
            }
            return r;
          });
        });
      }
      ToastQueue.info("Product removed from selection", { timeout: 2000 });
    },
    [],
  );

  // Handle row update (e.g., quantity change) in selected products
  const handleSelectedRowUpdate = useCallback((updatedRow: TreeGridRow) => {
    setSelectedProducts((prev) =>
      prev.map((row) => {
        if (row.id === updatedRow.id) {
          // Recalculate prices based on new quantity
          const quantity = parseFloat(updatedRow.data.quantity) || 1;
          const unitPrice = parseFloat(updatedRow.data.unitPrice) || 0;
          const vatValue = parseFloat(updatedRow.data.vatValue) || 0;

          // Calculate total amount (unit price * quantity)
          const totalAmount = unitPrice * quantity;
          // Calculate amount with VAT
          const amountWithVat = totalAmount * (1 + vatValue / 100);

          return {
            ...updatedRow,
            data: {
              ...updatedRow.data,
              quantity: quantity,
              totalAmount: totalAmount,
              amountWithVat: amountWithVat,
            },
          };
        }
        return row;
      }),
    );
  }, []);

  // Save selected products to project items
  const handleSaveToProjectItems = useCallback(async () => {
    if (selectedProducts.length === 0) {
      ToastQueue.info("No products selected to save", { timeout: 3000 });
      return;
    }

    if (!projectId) {
      ToastQueue.negative("Please enter a Project ID", { timeout: 3000 });
      return;
    }

    setIsSaving(true);
    try {
      // Get only parent rows (not components)
      const parentRows = selectedProducts.filter(
        (row) => row.parentId === null,
      );

      for (const row of parentRows) {
        // Use updated quantity and recalculate prices
        const quantity = parseFloat(row.data.quantity) || 1;
        const unitPrice = parseFloat(row.data.unitPrice) || 0;
        const totalAmount = row.data.totalAmount || unitPrice * quantity;
        const amountWithVat = row.data.amountWithVat || totalAmount;

        const result = await itemService.createProjectItem(
          new CreateProjectItemRequest(
            store.Server,
            store.SessionId,
            generateProjectProductItem(
              "", // folderId
              projectId, // projectId
              "", // activityId
              row.data.productId, // partID
              row.data.productName, // productName
              quantity, // quantity
              unitPrice, // unitPrice
              totalAmount, // totalPrice
              amountWithVat, // totalAmountWithVat
              row.data.currency || "", // currency
              String(row.data.vatValue || 0), // vatValue
              String(row.data.purchasePrice || 0), // purchasePrice
              "", // attrib0
              "", // attrib0_id
              "", // attrib1
              "", // attrib1_id
              "", // attrib2
              "", // attrib2_id
              "", // supplier
              row.data.refId || "", // refId
              new Date().toISOString().split("T")[0], // billingDate (today's date),
              "0", // billingStatus
              { type: "2" }, // productType (2 = product)
            ),
          ),
        );

        if (result.result !== "OK") {
          throw new Error(`Failed to save ${row.data.productName}`);
        }
      }

      // Move selected products to project items
      setProjectItems((prev) => [...prev, ...selectedProducts]);
      setSelectedProducts([]);

      ToastQueue.positive(
        `Saved ${parentRows.length} product(s) to project items`,
        { timeout: 3000 },
      );
    } catch (error: any) {
      console.error("Error saving project items:", error);
      ToastQueue.negative(error.message || "Failed to save project items", {
        timeout: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [selectedProducts, projectId, store.Server, store.SessionId, itemService]);

  // Clear all selected products
  const handleClearSelected = useCallback(() => {
    setSelectedProducts([]);
    ToastQueue.info("Selection cleared", { timeout: 2000 });
  }, []);

  // Columns for Available Products grid (top)
  const availableProductsColumns: TreeGridColumn[] = useMemo(
    () => [
      {
        key: "productName",
        header: "Product Name",
        width: 250,
        editable: false,
        isTreeColumn: true,
        showFolderIcon: true,
      },
      {
        key: "refId",
        header: "SKU/Ref ID",
        width: 100,
        editable: false,
      },
      {
        key: "category",
        header: "Category",
        width: 120,
        editable: false,
      },
      {
        key: "unitPrice",
        header: "Unit Price",
        width: 100,
        editable: false,
        align: "right",
        format: (value: any, row: TreeGridRow) =>
          `${parseFloat(value || 0).toFixed(2)} ${row.data.currency}`,
      },
      {
        key: "amountWithVat",
        header: "Price with VAT",
        width: 120,
        editable: false,
        align: "right",
        format: (value: any, row: TreeGridRow) =>
          `${parseFloat(value || 0).toFixed(2)} ${row.data.currency}`,
      },
      {
        key: "vatName",
        header: "VAT",
        width: 80,
        editable: false,
      },
      {
        key: "manufacturer",
        header: "Manufacturer",
        width: 150,
        editable: false,
      },
      {
        key: "status",
        header: "Status",
        width: 100,
        editable: false,
      },
    ],
    [],
  );

  // Search products function for ComboBox
  const searchProducts = useCallback(
    async (filterText: string): Promise<ComboBoxItem[]> => {
      if (filterText.length < 2) {
        return [];
      }

      try {
        // Search by name and refId concurrently
        const [itemResp, itemRespByRefID]: any = await Promise.all([
          itemService.listItem(
            new ListItemRequest(store.Server, store.SessionId, 15, 0, {
              searchName: filterText.trim(),
              statusShow: "1",
            }),
          ),
          itemService.listItem(
            new ListItemRequest(store.Server, store.SessionId, 15, 0, {
              refId: filterText.trim(),
              statusShow: "1",
            }),
          ),
        ]);

        const results: ComboBoxItem[] = [];

        // Format items from refId search
        if (itemRespByRefID.ITEM?.length) {
          itemRespByRefID.ITEM.forEach((item: any) => {
            results.push({
              id: `${item.id}_ref`,
              label: `${item.refId} (${item.name})`,
              sku: item.refId,
              unitPrice: parseFloat(item.amount) || 0,
              catalogPrice: parseFloat(item.amountWithVat) || 0,
              category: "SKU Match",
              // Store original data for population
              productId: item.id,
              productName: item.name,
              amountWithVat: parseFloat(item.amountWithVat) || 0,
              vatValue: parseFloat(item.vatValue) || 0,
              purchasePrice: parseFloat(item.purchasePrice) || 0,
              currency: item.currencyName || "CZK",
            });
          });
        }

        // Format items from name search
        if (itemResp.ITEM?.length) {
          itemResp.ITEM.forEach((item: any) => {
            // Avoid duplicates
            if (!results.some((r) => r.productId === item.id)) {
              results.push({
                id: `${item.id}_name`,
                label: item.name + (item.refId ? ` (${item.refId})` : ""),
                sku: item.refId || "",
                unitPrice: parseFloat(item.amount) || 0,
                catalogPrice: parseFloat(item.amountWithVat) || 0,
                category: "Name Match",
                // Store original data for population
                productId: item.id,
                productName: item.name,
                amountWithVat: parseFloat(item.amountWithVat) || 0,
                vatValue: parseFloat(item.vatValue) || 0,
                purchasePrice: parseFloat(item.purchasePrice) || 0,
                currency: item.currencyName || "CZK",
              });
            }
          });
        }

        return results;
      } catch (error) {
        console.error("Product search error:", error);
        return [];
      }
    },
    [itemService, store.Server, store.SessionId],
  );

  // Columns for Selected Products grid (middle)
  const selectedProductsColumns: TreeGridColumn[] = useMemo(
    () => [
      {
        key: "productName",
        header: "Product Name",
        width: 300,
        editable: true,
        editorType: "combobox",
        isTreeColumn: true,
        showFolderIcon: true,
        comboBoxConfig: {
          placeholder: "Search products by name or SKU...",
          menuWidth: 450,
          debounceMs: 250,
          minSearchLength: 2,

          // Async load function - searches products from API
          loadItems: searchProducts,

          // Group results by category (SKU Match vs Name Match)
          groupBy: (item) => item.category || "Other",

          // What to show in the input after selection
          getDisplayValue: (item) => item.productName || item.label,

          // What value to store in the cell
          getValue: (item) => item.productName || item.label,

          // Update multiple fields when a product is selected
          onSelectionChange: (item, row) => {
            if (!item) return {};
            return {
              productName: item.productName || item.label,
              refId: item.sku || "",
              productId: item.productId,
              unitPrice: item.unitPrice || 0,
              amountWithVat: item.amountWithVat || item.catalogPrice || 0,
              vatValue: item.vatValue || 0,
              purchasePrice: item.purchasePrice || 0,
              currency: item.currency || "CZK",
              totalAmount: (row.data.quantity || 1) * (item.unitPrice || 0),
            };
          },
        },
      },
      {
        key: "refId",
        header: "SKU/Ref ID",
        width: 100,
        editable: false,
      },
      {
        key: "quantity",
        header: "Quantity",
        width: 80,
        editable: true,
        editorType: "number",
        align: "right",
        numberFormat: {
          decimals: 2,
        },
      },
      {
        key: "unitPrice",
        header: "Unit Price",
        width: 100,
        editable: false,
        align: "right",
        format: (value: any, row: TreeGridRow) =>
          `${parseFloat(value || 0).toFixed(2)} ${row.data.currency}`,
      },
      {
        key: "totalAmount",
        header: "Total Amount",
        width: 120,
        editable: false,
        align: "right",
        showTotal: true,
        format: (value: any, row: TreeGridRow) => {
          const total = row.data.totalAmount || row.data.unitPrice;
          return `${parseFloat(total || 0).toFixed(2)} ${row.data.currency}`;
        },
        render: (value: any, row: TreeGridRow) => {
          const total = row.data.totalAmount || row.data.unitPrice;
          return (
            <strong>
              {parseFloat(total || 0).toFixed(2)} {row.data.currency}
            </strong>
          );
        },
      },
      {
        key: "amountWithVat",
        header: "With VAT",
        width: 120,
        editable: false,
        align: "right",
        showTotal: true,
        format: (value: any) => `${parseFloat(value || 0).toFixed(2)}`,
        render: (value: any, row: TreeGridRow) => {
          return (
            <strong>
              {parseFloat(value || 0).toFixed(2)} {row.data.currency}
            </strong>
          );
        },
      },
      {
        key: "category",
        header: "Category",
        width: 120,
        editable: false,
      },
      {
        key: "vatName",
        header: "VAT",
        width: 80,
        editable: false,
      },
    ],
    [searchProducts],
  );

  // Columns for Project Items grid (bottom)
  const projectItemsColumns: TreeGridColumn[] = useMemo(
    () => [
      {
        key: "productName",
        header: "Product Name",
        width: 250,
        editable: false,
        isTreeColumn: true,
        showFolderIcon: true,
      },
      {
        key: "refId",
        header: "SKU/Ref ID",
        width: 100,
        editable: false,
      },
      {
        key: "quantity",
        header: "Qty",
        width: 70,
        editable: false,
        align: "right",
      },
      {
        key: "unitPrice",
        header: "Unit Price",
        width: 100,
        editable: false,
        align: "right",
        format: (value: any, row: TreeGridRow) =>
          `${parseFloat(value || 0).toFixed(2)} ${row.data.currency}`,
      },
      {
        key: "totalAmount",
        header: "Total",
        width: 120,
        editable: false,
        align: "right",
        showTotal: true,
        render: (value: any, row: TreeGridRow) => {
          const total = row.data.totalAmount || row.data.unitPrice;
          return (
            <strong>
              {parseFloat(total || 0).toFixed(2)} {row.data.currency}
            </strong>
          );
        },
      },
      {
        key: "amountWithVat",
        header: "With VAT",
        width: 120,
        editable: false,
        align: "right",
        showTotal: true,
        render: (value: any, row: TreeGridRow) => {
          return (
            <strong>
              {parseFloat(value || 0).toFixed(2)} {row.data.currency}
            </strong>
          );
        },
      },
      {
        key: "category",
        header: "Category",
        width: 120,
        editable: false,
      },
    ],
    [],
  );

  // Calculate totals for selected products
  const selectedTotals = useMemo(() => {
    const parentRows = selectedProducts.filter((r) => r.parentId === null);
    const total = parentRows.reduce(
      (sum, row) => sum + (row.data.totalAmount || row.data.unitPrice || 0),
      0,
    );
    const totalWithVat = parentRows.reduce(
      (sum, row) => sum + (row.data.amountWithVat || 0),
      0,
    );
    return { total, totalWithVat, count: parentRows.length };
  }, [selectedProducts]);

  return (
    <Provider theme={defaultTheme}>
      <div
        style={{
          padding: "24px",
          maxWidth: "1600px",
          margin: "0 auto",
          position: "relative",
        }}
      >
        {/* Loading Overlay */}
        {(isLoadingProducts || isLoadingSelected || isSaving) && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
          >
            <div
              style={{
                background: "white",
                padding: "24px 48px",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "16px",
              }}
            >
              <ProgressCircle aria-label="Loading" isIndeterminate />
              <div style={{ fontSize: "16px", fontWeight: 500 }}>
                {isSaving ? "Saving..." : "Loading..."}
              </div>
            </div>
          </div>
        )}

        <h1 style={{ marginBottom: "16px" }}>
          Product Selection & Project Items
        </h1>

        {/* Instructions Panel */}
        <div
          style={{
            background: "#e3f2fd",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "24px",
            border: "1px solid #2196f3",
          }}
        >
          <h3 style={{ marginTop: 0, color: "#1565c0" }}>Instructions</h3>
          <ol
            style={{
              margin: "8px 0",
              fontSize: "14px",
              color: "#0d47a1",
              paddingLeft: "20px",
            }}
          >
            <li>
              Drag products from the <strong>Available Products</strong> grid to
              the <strong>Selected Products</strong> grid
            </li>
            <li>Products with components will show child rows automatically</li>
            <li>Edit quantities in the Selected Products grid if needed</li>
            <li>Use the delete icon (🗑️) to remove products from selection</li>
            <li>
              Enter a Project ID and click <strong>Save to Project</strong> to
              add items to the project
            </li>
          </ol>
        </div>

        {/* Project ID Input */}
        <Flex gap="size-200" alignItems="end" marginBottom="size-300">
          <View UNSAFE_style={{ display: "none" }}>
            <label
              style={{ display: "block", marginBottom: "4px", fontWeight: 600 }}
            >
              Project ID:
            </label>
            <input
              type="text"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              placeholder="Enter Project ID"
              style={{
                padding: "8px 12px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
                width: "200px",
              }}
            />
          </View>
          <Button variant="primary" onPress={() => loadProducts(true)}>
            Refresh Products
          </Button>
        </Flex>

        {/* Top Grid: Available Products */}
        <div style={{ marginBottom: "24px" }}>
          <div
            style={{
              border: "1px solid #ddd",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <TreeGridComponent
              title="Available Products (Catalog)"
              gridId="available-products"
              columns={availableProductsColumns}
              data={availableProducts}
              showSelection
              showSelectAll
              enableDragDrop
              disableInternalDrop // Prevent reordering within the catalog
              enableKeyboardNav
              paginationMode="client"
              pageSize={PAGE_SIZE}
              indentPerLevel={32}
              maxDepth={1}
              showGridLines
              showHover
              showToolbar={false}
              showInfoIcon={false}
              showDeleteIcon={false}
              totalRows={totalProducts}
              hasMore={hasMoreProducts}
              isLoadingMore={isLoadingMore}
              onLoadMore={loadMoreProducts}
              maxHeight={350}
              emptyMessage={
                isLoadingProducts
                  ? "Loading products..."
                  : "No products available"
              }
            />
          </div>
        </div>

        {/* Arrow indicator */}
        <div
          style={{
            textAlign: "center",
            padding: "8px",
            fontSize: "24px",
            color: "#666",
          }}
        >
          ⬇️ Drag products here ⬇️
        </div>

        {/* Middle Grid: Selected Products */}
        <div style={{ marginBottom: "24px" }}>
          <div
            style={{
              border: "2px solid #4caf50",
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 0 0 2px rgba(76, 175, 80, 0.2)",
            }}
          >
            <TreeGridComponent
              title={`Selected Products (${selectedTotals.count} items)`}
              gridId="selected-products"
              columns={selectedProductsColumns}
              data={selectedProducts}
              showSelection={false}
              showSelectAll={false}
              enableDragDrop={false}
              enableKeyboardNav
              enableInlineEdit
              pageSize={20}
              indentPerLevel={32}
              maxDepth={2}
              showGridLines
              showHover
              showToolbar={true}
              showInfoIcon={false}
              showDeleteIcon
              acceptExternalDrop
              acceptDropFromGridIds={["available-products"]}
              onExternalDrop={handleExternalDropToSelected}
              onDeleteClick={handleDeleteFromSelected}
              onRowUpdate={handleSelectedRowUpdate}
              onRowCreate={(parentId, rowType) => {
                // Create a new row with default values
                const newRow: TreeGridRow = {
                  id: `new-${Date.now()}`,
                  parentId: parentId,
                  depth: parentId ? 1 : 0,
                  childrenIds: [],
                  isExpanded: false,
                  rowType: "product",
                  sortOrder: selectedProducts.length,
                  data: {
                    productName: "",
                    refId: "",
                    productId: "",
                    quantity: 1,
                    unitPrice: 0,
                    totalAmount: 0,
                    amountWithVat: 0,
                    vatValue: 0,
                    purchasePrice: 0,
                    currency: "CZK",
                    category: "",
                    vatName: "",
                  },
                };
                setSelectedProducts((prev) => [...prev, newRow]);
              }}
              emptyMessage="Click 'Add Row' or drag products here to add them"
              deleteConfirmationMessage={
                "Are you sure you want to remove this product from selection?"
              }
              deleteConfirmationTitle="Remove product"
            />
          </div>

          {/* Selection Summary & Actions */}
          <Flex
            justifyContent="space-between"
            alignItems="center"
            marginTop="size-200"
            UNSAFE_style={{
              background: "#f5f5f5",
              padding: "12px 16px",
              borderRadius: "4px",
            }}
          >
            <div>
              <strong>Selection Summary:</strong> {selectedTotals.count}{" "}
              product(s) | Total:{" "}
              <strong>{selectedTotals.total.toFixed(2)}</strong> | With VAT:{" "}
              <strong>{selectedTotals.totalWithVat.toFixed(2)}</strong>
            </div>
            <Flex gap="size-100">
              <Button
                variant="secondary"
                onPress={handleClearSelected}
                isDisabled={selectedProducts.length === 0}
              >
                Clear Selection
              </Button>
              <Button
                variant="cta"
                onPress={handleSaveToProjectItems}
                isDisabled={
                  selectedProducts.length === 0 || !projectId || isSaving
                }
              >
                💾 Save to Project
              </Button>
            </Flex>
          </Flex>
        </div>

        {/* Bottom Grid: Project Items */}
        <div>
          <div
            style={{
              border: "1px solid #1976d2",
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
            }}
          >
            <TreeGridComponent
              title={`Project Items (Saved - ${
                projectItems.filter((r) => r.parentId === null).length
              } items)`}
              gridId="project-items"
              columns={projectItemsColumns}
              data={projectItems}
              showSelection={false}
              enableDragDrop={false}
              enableKeyboardNav
              pageSize={20}
              indentPerLevel={32}
              maxDepth={2}
              showGridLines
              showHover
              showToolbar={false}
              showInfoIcon={true}
              showDeleteIcon={false}
              onInfoClick={(rowId, row) => {
                ToastQueue.info(
                  `Product: ${row.data.productName} - ${
                    row.data.refId || "No SKU"
                  }`,
                  {
                    timeout: 3000,
                  },
                );
              }}
              emptyMessage="No project items yet. Save selected products to add them here."
            />
          </div>
        </div>

        {/* Feature List */}
        <div
          style={{
            marginTop: "32px",
            padding: "16px",
            background: "#f9f9f9",
            borderRadius: "8px",
          }}
        >
          <h3>Features Demonstrated</h3>
          <ul style={{ columnCount: 2, columnGap: "32px" }}>
            <li>✅ Three-grid layout with drag & drop</li>
            <li>✅ Live product data from API</li>
            <li>✅ Product components as child rows</li>
            <li>✅ Automatic price calculation</li>
            <li>✅ Delete from selection</li>
            <li>✅ Save to project items</li>
            <li>✅ Selection summary with totals</li>
            <li>✅ Quantity editing</li>
            <li>✅ Expand/collapse for components</li>
            <li>✅ Loading states</li>
          </ul>
        </div>
      </div>
    </Provider>
  );
}
